import { Link } from 'react-router';
import { Juridisk } from '../components/Juridisk';
import { SELSKAP, prisavsnitt } from '../lib/selskap';
import { SIDETITLER, useSidehode } from '../lib/sidehode';

/**
 * Salgsbetingelser.
 *
 * Bygget på angrerettloven og forbrukerkjøpsreglene, og på oppsettet
 * Forbrukertilsynet anbefaler: hvem selger er, hva som selges, hva det koster,
 * hvordan det betales og leveres, hvordan man angrer, og hvor man klager.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  Ett valg som er tatt her, og som er verdt å se på:
 *
 *  Tjenesten låses opp i det betalingen er bekreftet. Angrerettloven § 22 n
 *  åpner for at angreretten kan falle bort ved digitalt innhold som leveres med
 *  det samme, dersom kunden uttrykkelig samtykker til det og erkjenner å miste
 *  angreretten.
 *
 *  Vi har valgt å *ikke* be om det samtykket. Kunden får full angrerett i 14
 *  dager, selv om tilgangen åpnes med én gang. Grunnen er dobbel: et samtykke
 *  som ikke er innhentet helt riktig, er ugyldig uansett — da har man friksjonen
 *  i kjøpet og risikoen i tillegg — og «14 dagers angrerett, uansett» er lettere
 *  å si i en kjøpsknapp enn et avkryssingsfelt om bortfall av rettigheter.
 *
 *  Skal det snus, er det denne siden og avkryssingen i kjøpsflyten som endres.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function SalgsbetingelserPage() {
  useSidehode(SIDETITLER.salgsbetingelser, '/salgsbetingelser');
  return (
    <Juridisk
      tittel="Salgsbetingelser"
      ingress={
        <p>
          Disse betingelsene gjelder når du kjøper en pakke i {SELSKAP.tjeneste}. Bruken av
          tjenesten reguleres i tillegg av <Link to="/vilkaar">bruksvilkårene</Link>. Er du
          forbruker, gjelder ufravikelige regler i forbrukerkjøpsloven, angrerettloven og
          markedsføringsloven uansett hva som står her.
        </p>
      }
    >
      <h2>1. Partene</h2>
      <p>
        Selger er {SELSKAP.navn}, som under kalles «vi». Kjøper er den som er oppgitt som kjøper
        ved bestillingen, under kalt «du». Fullstendige selskapsopplysninger står nederst på siden.
      </p>

      <h2>2. Hva du kjøper</h2>
      <p>
        Du kjøper tilgang til en pakke i {SELSKAP.tjeneste} for <strong>ett bryllup</strong>.
        Hva hver pakke inneholder, står på{' '}
        <Link to="/app/abonnement">prissiden</Link> og på forsiden. Kjøpet er et{' '}
        <strong>engangskjøp</strong> — ikke et løpende abonnement. Det fornyes ikke, og det trekkes
        ikke penger på nytt.
      </p>
      <p>
        Tilgangen varer så lenge tjenesten drives og kontoen består. Legges tjenesten ned, varsler vi
        minst tre måneder i forveien, jf. <Link to="/vilkaar">bruksvilkårene</Link> punkt 8.
      </p>

      <h2>3. Pris</h2>
      <p>
        Prisen som står oppgitt ved bestillingen, er den totale prisen du skal betale. Alle priser
        er i norske kroner.
      </p>
      <p>
        {prisavsnitt()} Det kommer ingen gebyrer eller tillegg i tillegg til den oppgitte prisen.
      </p>

      <h2>4. Bestilling og avtaleinngåelse</h2>
      <p>
        Avtalen er bindende når bestillingen er registrert hos oss og betalingen er bekreftet av
        betalingsleverandøren. Vi sender en ordrebekreftelse på e-post. Kontroller at den stemmer
        med bestillingen, og si fra hvis noe avviker.
      </p>
      <p>
        Er du under 18 år, kan du ikke handle her uten samtykke fra foreldre eller foresatte.
      </p>

      <h2>5. Betaling</h2>
      <p>
        Betaling skjer med de betalingsmåtene som er tilgjengelige ved bestillingen. Vi håndterer
        ikke kortopplysninger selv — betalingen gjennomføres hos betalingsleverandøren, som er
        underlagt sine egne vilkår og sikkerhetskrav. Beløpet belastes ved bestilling.
      </p>

      <h2>6. Levering</h2>
      <p>
        Tjenesten er digital og leveres ved at pakken låses opp på kontoen din. Det skjer normalt
        umiddelbart etter at betalingen er bekreftet. Skjer det ikke innen kort tid, ta kontakt på{' '}
        <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a>.
      </p>

      <h2>7. Angrerett</h2>
      <p>
        <strong>Du har 14 dagers angrerett.</strong> Fristen løper fra dagen etter at avtalen ble
        inngått.
      </p>
      <p>
        Angreretten gjelder <em>selv om du har tatt tjenesten i bruk</em>. Vi ber deg ikke om å gi
        avkall på angreretten mot å få tilgang med det samme, slik angrerettloven § 22 bokstav n
        åpner for. Du kan altså prøve tjenesten i angrefristen og likevel angre.
      </p>
      <p>
        For å bruke angreretten må du gi oss beskjed innen fristen. Det holder å sende en e-post
        til <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a> der det går klart fram at du
        vil gå fra avtalen.
      </p>
      <p>
        Vil du heller bruke det standardiserte angreskjemaet, ligger det{' '}
        <Link to="/vilkaar/angrerett">her</Link>, til utfylling. Adressen står også i
        ordrebekreftelsen du fikk på e-post, og under Abonnement så lenge fristen løper. Du kan
        også hente skjemaet{' '}
        <a
          href="https://www.forbrukertilsynet.no/angrerettskjema"
          target="_blank"
          rel="noreferrer"
        >
          hos Forbrukertilsynet
        </a>
        . Skjemaet er et tilbud, ikke et krav — en e-post holder.
      </p>
      <p>
        Vi betaler tilbake hele beløpet uten unødig opphold, og senest 14 dager etter at vi mottok
        meldingen. Tilbakebetalingen skjer til den samme betalingsmåten du brukte, med mindre vi
        blir enige om noe annet. Når du angrer, faller tilgangen til den betalte pakken bort;
        gjestelista og RSVP, som er gratis, beholder du.
      </p>

      <h2>8. Forsinkelse og manglende levering</h2>
      <p>
        Får du ikke tilgang til det du har betalt for, kan du holde kjøpesummen tilbake, kreve
        levering, kreve erstatning eller heve avtalen etter reglene i forbrukerkjøpsloven. Ta
        kontakt med oss først — det pleier å løse seg raskest slik.
      </p>

      <h2>9. Reklamasjon</h2>
      <p>
        Virker ikke tjenesten som den skal, gi oss beskjed innen rimelig tid etter at du oppdaget
        det. Fristen for å reklamere er to år. Vi retter feilen, og lar den seg ikke rette, kan du
        kreve prisavslag eller heve kjøpet.
      </p>
      <p>
        Reklamasjon sendes til <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a>. Beskriv
        gjerne hva du gjorde, hva du forventet, og hva som skjedde i stedet.
      </p>

      <h2>10. Personopplysninger</h2>
      <p>
        Vi behandler opplysninger om deg for å gjennomføre kjøpet og levere tjenesten.{' '}
        <Link to="/personvern">Personvernerklæringen</Link> forklarer hva som lagres, hvorfor, og
        hvordan du får innsyn, retting eller sletting.
      </p>

      <h2>11. Konfliktløsning</h2>
      <p>
        Ta kontakt med oss først. Blir vi ikke enige, kan du klage til{' '}
        <a href="https://www.forbrukerradet.no" target="_blank" rel="noreferrer">
          Forbrukerrådet
        </a>
        , som mekler i saker mellom forbrukere og næringsdrivende. Du kan også bruke EU-kommisjonens
        klageportal:{' '}
        <a href="https://ec.europa.eu/odr" target="_blank" rel="noreferrer">
          ec.europa.eu/odr
        </a>
        .
      </p>
      <p>
        Klage på markedsføring eller på selve avtalevilkårene rettes til{' '}
        <a href="https://www.forbrukertilsynet.no" target="_blank" rel="noreferrer">
          Forbrukertilsynet
        </a>
        .
      </p>
      <p>Avtalen reguleres av norsk rett.</p>
    </Juridisk>
  );
}
